import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl, useIntl } from 'react-intl';
import { FaUndo } from 'react-icons/fa';
import classnames from 'classnames';
import momentPropTypes from 'react-moment-proptypes';
import moment from 'moment';
import Toggle from 'react-toggle';
import intlShape from '@/shared/utils/intlShape';
import { hasMultipleCompanies } from '@/shared/components/security/HasMultiCompany';
import CompanyMultiSelect from '@/shared/components/input/multiselect/CompanyMultiSelect';
import { TaskTemplateMultiSelect } from '@/shared/components/input/multiselect/TaskTemplateMultiSelect';
import { listItemMsg } from '@/shared/components/FormattedListValue';
import api from '@/shared/api';
import { dynamicSort } from '@/shared/utils/dynamicSort';
import AuthService from '@/shared/services/AuthService';
import DataCacheService from '@/shared/services/DataCacheService';
import ClearButtonWrapper from '@/shared/components/ClearButtonWrapper';
import DelayedDateRangePicker from '@/shared/components/input/DelayedDateRangePicker';
import TeamMultiSelect from '@/shared/components/input/multiselect/TeamMultiSelect';
import PeriodButtonGroup, { getPeriodStartEndDates } from '@/shared/components/PeriodButtonGroup';
import UserSearch from '@/shared/components/input/UserSearch';
import guid from '@/shared/utils/guid';
import EndFiscalYearMultiSelect from '@/shared/components/input/multiselect/EndFiscalYearMultiSelect';
import SavedFiltersButton from '@/shared/components/SavedFiltersButton';
import config from '../../../config';

import messages from '../taskMessages';

const periods = ['all', 'month', 'nextMonth', 'quarter', 'nextQuarter', 'year'];

export const defaultFilters = {
  deadlineUntil: null,
  deadlineFrom: null,
  period: null,
  endFiscalYears: [],
  ownCompanies: [],
  userId: null,
  username: null,
  createdBy: null,
  taskTemplates: [],
  periodicityId: null,
  teams: [],
  hideCompleted: false,
};

class TaskDashboardFilters extends Component {
  state = {
    periodicities: [],
  };

  static propTypes = {
    onFilterChange: PropTypes.func.isRequired,
    filters: PropTypes.shape({
      deadlineUntil: momentPropTypes.momentObj,
      deadlineFrom: momentPropTypes.momentObj,
      endFiscalYears: PropTypes.arrayOf(PropTypes.string),
      period: PropTypes.oneOf(periods),
      ownCompanies: PropTypes.arrayOf(PropTypes.string),
      userId: PropTypes.string,
      createdBy: PropTypes.string,
      taskTemplates: PropTypes.arrayOf(PropTypes.string),
      periodicityId: PropTypes.string,
      teams: PropTypes.arrayOf(PropTypes.number),
      hideCompleted: PropTypes.bool,
    }),
    showPercentages: PropTypes.bool.isRequired,
    onChartTypeChange: PropTypes.func.isRequired,
    intl: intlShape.isRequired,
  };

  static defaultProps = {
    filters: defaultFilters,
  };

  componentDidMount() {
    const { filters } = this.props;

    if (filters && filters.period) {
      this.setPeriod(filters.period);
    }
    if (filters && filters.endOfFiscalYearPeriod) {
      this.setEndOfFiscalYearPeriod(filters.endOfFiscalYearPeriod);
    }
    // Load periodicities if exactly one template is selected
    if (filters && filters.taskTemplates && filters.taskTemplates.length === 1) {
      this.retrievePeriodicities(filters.taskTemplates[0]);
    }
  }

  componentDidUpdate(prevProps) {
    const { filters: newFilters } = this.props;
    const { filters: oldFilters } = prevProps;

    // Load periodicities only when exactly one template is selected
    const oldTemplates = oldFilters.taskTemplates || [];
    const newTemplates = newFilters.taskTemplates || [];

    if (JSON.stringify(oldTemplates) !== JSON.stringify(newTemplates)) {
      if (newTemplates.length === 1) {
        this.retrievePeriodicities(newTemplates[0]);
      } else {
        // Clear periodicities and selected periodicity if not exactly one template
        this.setState({ periodicities: [] });
        if (newFilters.periodicityId) {
          this.filterChanged({ periodicityId: null });
        }
      }
    }
  }

  filterChanged = filter => {
    const { filters, onFilterChange } = this.props;
    const newFilters = { ...filters, ...filter };

    onFilterChange(newFilters);
  };

  deadlineChanged = (startDate, endDate) => {
    this.filterChanged({ deadlineFrom: startDate, deadlineUntil: endDate, period: null });
  };

  endFiscalYearChanged = (dateFrom, dateUntil) => {
    this.filterChanged({
      endOfFiscalYearFrom: dateFrom,
      endOfFiscalYearUntil: dateUntil,
      endOfFiscalYearPeriod: null,
    });
  };

  setPeriod = value => {
    const [deadlineFrom, deadlineUntil, period] = getPeriodStartEndDates(value);
    this.filterChanged({ deadlineFrom, deadlineUntil, period });
  };
  setEndOfFiscalYearPeriod = value => {
    const [
      endOfFiscalYearFrom,
      endOfFiscalYearUntil,
      endOfFiscalYearPeriod,
    ] = getPeriodStartEndDates(value);
    this.filterChanged({ endOfFiscalYearFrom, endOfFiscalYearUntil, endOfFiscalYearPeriod });
  };
  periodChanged = ({ dateFrom, dateUntil, period }) => {
    this.filterChanged({ deadlineFrom: dateFrom, deadlineUntil: dateUntil, period });
  };

  endFiscalYearPeriodChanged = ({ dateFrom, dateUntil, period }) => {
    this.filterChanged({
      endOfFiscalYearFrom: dateFrom,
      endOfFiscalYearUntil: dateUntil,
      endOfFiscalYearPeriod: period,
    });
  };

  retrievePeriodicities = templateId => {
    const { intl } = this.props;
    if (!templateId) {
      this.setState({ periodicities: [] });
      return;
    }
    return api.base
      .get(`${config.appUrls.tasks}/api/TaskPeriodicity/${templateId}`)
      .then(({ data }) => {
        const translatedPeriodicities = data
          .map(t => ({
            ...t,
            label: intl.formatMessage(listItemMsg('taskperiodicity', t.id, t.name)), // Map translation to label property
          }))
          .sort(dynamicSort('label'));

        this.setState({
          periodicities: translatedPeriodicities,
        });
      });
  };
  userChanged = item => {
    if (!item || !item.id) {
      this.filterChanged({ userId: null, username: null });
    } else {
      this.filterChanged({
        userId: item.id,
        username: item.name,
      });
    }
  };
  createdByChanged = user => {
    this.filterChanged({ createdBy: user ? user.id : null });
  };
  resetFilters = () => {
    const { onFilterChange } = this.props;
    onFilterChange({ ...defaultFilters });
  };

  savedFilterChanged = ({ showPercentages, ...filters }) => {
    const { onFilterChange, onChartTypeChange } = this.props;
    const newFilters = { ...defaultFilters, ...filters };

    if (typeof newFilters.deadlineFrom === 'string')
      newFilters.deadlineFrom = moment.utc(filters.deadlineFrom);
    if (typeof newFilters.deadlineUntil === 'string')
      newFilters.deadlineUntil = moment.utc(filters.deadlineUntil);

    if (newFilters.period) {
      const [deadlineFrom, deadlineUntil] = getPeriodStartEndDates(newFilters.period);
      newFilters.deadlineFrom = deadlineFrom;
      newFilters.deadlineUntil = deadlineUntil;
    }

    onFilterChange(newFilters);

    if (typeof showPercentages === 'boolean') {
      onChartTypeChange(showPercentages);
    }

    if (newFilters.taskTemplates && newFilters.taskTemplates.length === 1) {
      this.retrievePeriodicities(newFilters.taskTemplates[0]);
    } else {
      this.setState({ periodicities: [] });
    }
  };

  render() {
    const { periodicities } = this.state;
    const { filters, intl, showPercentages, onChartTypeChange } = this.props;
    const showCompanies = hasMultipleCompanies();

    const hasFilters = JSON.stringify(filters) !== JSON.stringify(defaultFilters);
    const isPeriodicityDisabled =
      !filters.taskTemplates ||
      filters.taskTemplates.length !== 1 ||
      !periodicities ||
      periodicities.length === 0;

    // Prepare filters for saving/loading
    const defaultFiltersForSaving = {
      ...defaultFilters,
      showPercentages: false,
    };

    const currentFiltersForSaving = {
      ...filters,
      showPercentages,
    };

    return (
      <div className="info-block container-fluid">
        <div className="row">
          <div className="col-md-3">
            <div className="form-group">
              <DelayedDateRangePicker
                startDate={filters.deadlineFrom}
                startDateId="TaskOverviewFilterDeadlineFrom"
                endDate={filters.deadlineUntil}
                endDateId="TaskOverviewFilterDeadlineUntil"
                onDatesChange={({ startDate, endDate }) => this.deadlineChanged(startDate, endDate)}
                startDatePlaceholderText={intl.formatMessage(messages.DeadlineFrom)}
                endDatePlaceholderText={intl.formatMessage(messages.DeadlineUntil)}
              />
            </div>
          </div>
          <div className="col-md-3 col-md-offset-6">
            <div className="form-group">
              <label className="margin5R margin 10T">
                <FormattedMessage {...messages.ChartType} />:
              </label>
              <div role="group" aria-label="crmType" className="btn-group pull-right">
                <button
                  onClick={() => onChartTypeChange(false)}
                  type="button"
                  title={intl.formatMessage(messages.ShowQuantitiesChart)}
                  className={classnames('btn btn-default', {
                    active: !showPercentages,
                  })}
                >
                  {intl.formatMessage(messages.Quantities)}
                </button>
                <button
                  onClick={() => onChartTypeChange(true)}
                  type="button"
                  title={intl.formatMessage(messages.ShowPercentagesChart)}
                  className={classnames('btn btn-default', {
                    active: showPercentages,
                  })}
                >
                  {intl.formatMessage(messages.Percentages)}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-md-6">
            <div className="form-group">
              <PeriodButtonGroup
                dateFrom={filters.deadlineFrom}
                dateUntil={filters.deadlineFrom}
                period={filters.period}
                visiblePeriods={periods}
                onChange={this.periodChanged}
              />
            </div>
          </div>
          <div className="col-md-6">
            <div className="form-group">
              <TeamMultiSelect
                selection={filters.teams}
                onChange={teams => this.filterChanged({ teams })}
                idProp="code"
              />
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-md-3 col-sm-6">
            <div className="form-group">
              <TaskTemplateMultiSelect
                selection={filters.taskTemplates || []}
                onChange={taskTemplates => this.filterChanged({ taskTemplates })}
                placeholder={intl.formatMessage(messages.AllTasks)}
              />
            </div>
          </div>
          <div className="col-md-3 col-sm-6">
            <div className="form-group">
              <ClearButtonWrapper
                show={!!filters.periodicityId}
                onClear={() => this.filterChanged({ periodicityId: null })}
              >
                <select
                  onChange={e => this.filterChanged({ periodicityId: e.target.value || null })}
                  className="form-control"
                  disabled={isPeriodicityDisabled}
                  value={filters.periodicityId || ''}
                >
                  <option value="">
                    {filters.periodicityId
                      ? intl.formatMessage(messages.ClearFilter)
                      : intl.formatMessage(messages.FilterPeriodicity)}
                  </option>
                  <option
                    style={{ fontSize: '1pt' }}
                    className="grey"
                    disabled="disabled"
                    value="&nbsp;"
                  >
                    &nbsp;
                  </option>
                  {periodicities
                    ? periodicities.map(periodicity => (
                        <option key={periodicity.id} value={periodicity.id}>
                          {periodicity.label}
                        </option>
                      ))
                    : null}
                </select>
              </ClearButtonWrapper>
            </div>
          </div>
          <div className="col-md-3 col-sm-6">
            <div className="form-group">
              <UserSearch
                placeholder={intl.formatMessage(messages.TasksAssignedTo)}
                onChange={this.userChanged}
                value={filters.userId}
                withCurrentUser
                withNotAssignedToMe
                withUnassigned
                unassignedId={guid.empty}
              />
            </div>
          </div>
          <div className="col-md-3 col-sm-6">
            <div className="form-group">
              <UserSearch
                placeholder={intl.formatMessage(messages.CreatedBy)}
                onChange={this.createdByChanged}
                value={filters.createdBy}
                withByPlanning
              />
            </div>
          </div>
          {showCompanies && (
            <div className="col-md-3 col-sm-6">
              <div className="form-group">
                <CompanyMultiSelect
                  selection={filters.ownCompanies}
                  onChange={companies => this.filterChanged({ ownCompanies: companies })}
                />
              </div>
            </div>
          )}

          <div className="col-md-3 margin5B">
            <EndFiscalYearMultiSelect
              selection={filters.endFiscalYears}
              onChange={endFiscalYears => this.filterChanged({ endFiscalYears })}
            />
          </div>
        </div>

        <div className="row">
          <div className="col-md-12">
            <div className="form-group">
              <label className="react-toggle-wrapper margin10T">
                <Toggle
                  checked={filters.hideCompleted || false}
                  onChange={e => this.filterChanged({ hideCompleted: e.target.checked })}
                />
                <span className="label-text">
                  <FormattedMessage {...messages.HideCompletedTasks} />
                </span>
              </label>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-md-12">
            <SavedFiltersButton
              screen="TaskDashboard"
              currentFilters={currentFiltersForSaving}
              defaultFilters={defaultFiltersForSaving}
              onFiltersChanged={this.savedFilterChanged}
              ignoreFilters={['username']}
            />
          </div>
        </div>
      </div>
    );
  }
}

export default injectIntl(TaskDashboardFilters);
